import React, { useCallback, useMemo, useState } from "react";
import { AxiosError } from "axios";
import moment from "moment";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  getOutfitDetailsPostCount,
  OutfitPostCountCategory,
  OutfitPostCountGender,
} from "../../app/api/outfitDetailsPostCount";
import Dropdown from "../../components/Common/Dropdown";

type Props = {
  isSidebarOpen: boolean;
};

type CountPair = {
  withOutfit: number | null;
  total: number | null;
};

type PostCountResult = {
  byMetaPostId: CountPair;
  byInstaShortcode: CountPair;
};

type SubmittedFilters = {
  monthLabel: string;
  genders: OutfitPostCountGender[];
  categories: OutfitPostCountCategory[];
};

const MONTHS = moment.months();
const GENDER_OPTIONS: { value: OutfitPostCountGender; label: string }[] = [
  { value: "Men", label: "Male" },
  { value: "Women", label: "Female" },
];
const CATEGORY_OPTIONS: OutfitPostCountCategory[] = ["Ethnic", "Western"];

type FilterCheckboxProps = {
  label: string;
  checked: boolean;
  onChange: () => void;
};

const FilterCheckbox: React.FC<FilterCheckboxProps> = ({
  label,
  checked,
  onChange,
}) => (
  <label className="inline-flex items-center cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="form-radio"
      style={{ accentColor: "#212121" }}
    />
    <span className="ml-2 text-sm text-secondary">{label}</span>
  </label>
);

type StatCardProps = {
  title: string;
  description: string;
  withOutfit: number | null;
  total: number | null;
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  description,
  withOutfit,
  total,
}) => (
  <article className="bg-white rounded-2xl border border-light shadow-sm p-5">
    <p className="text-xs uppercase tracking-wide text-tertiary mb-1">
      {title}
    </p>
    <p className="text-sm text-secondary mb-4">{description}</p>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <p className="text-xs text-tertiary mb-1">With outfit details</p>
        <p className="text-3xl font-semibold text-primary tabular-nums">
          {withOutfit !== null ? withOutfit.toLocaleString() : "—"}
        </p>
      </div>
      <div>
        <p className="text-xs text-tertiary mb-1">Total</p>
        <p className="text-3xl font-semibold text-primary tabular-nums">
          {total !== null ? total.toLocaleString() : "—"}
        </p>
      </div>
    </div>
  </article>
);

const OutfitPostCount: React.FC<Props> = ({ isSidebarOpen }) => {
  const currentYear = moment().year();
  const yearOptions = useMemo(
    () => Array.from({ length: 10 }, (_, index) => String(currentYear - index)),
    [currentYear]
  );

  const [selectedMonth, setSelectedMonth] = useState(moment().format("MMMM"));
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [selectedGenders, setSelectedGenders] = useState<
    OutfitPostCountGender[]
  >(["Men", "Women"]);
  const [selectedCategories, setSelectedCategories] = useState<
    OutfitPostCountCategory[]
  >(["Ethnic", "Western"]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [submittedFilters, setSubmittedFilters] =
    useState<SubmittedFilters | null>(null);
  const [counts, setCounts] = useState<PostCountResult>({
    byMetaPostId: { withOutfit: null, total: null },
    byInstaShortcode: { withOutfit: null, total: null },
  });

  const getMonthDateRange = useCallback((month: string, year: string) => {
    const monthMoment = moment(`${year}-${month}`, "YYYY-MMMM");
    return {
      startDate: monthMoment.clone().startOf("month").format("YYYY-MM-DD"),
      endDate: monthMoment.clone().endOf("month").format("YYYY-MM-DD"),
    };
  }, []);

  const toggleGender = (gender: OutfitPostCountGender) => {
    setSelectedGenders(prev =>
      prev.includes(gender)
        ? prev.filter(item => item !== gender)
        : [...prev, gender]
    );
  };

  const toggleCategory = (category: OutfitPostCountCategory) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(item => item !== category)
        : [...prev, category]
    );
  };

  const handleRun = async () => {
    if (selectedGenders.length === 0) {
      toast.error("Select at least one gender", { hideProgressBar: true });
      return;
    }

    if (selectedCategories.length === 0) {
      toast.error("Select at least one category", { hideProgressBar: true });
      return;
    }

    setLoading(true);
    setHasFetched(true);
    setSubmittedFilters({
      monthLabel: `${selectedMonth} ${selectedYear}`,
      genders: [...selectedGenders],
      categories: [...selectedCategories],
    });

    const { startDate, endDate } = getMonthDateRange(
      selectedMonth,
      selectedYear
    );

    try {
      const [byMetaPostId, byInstaShortcode] = await Promise.all([
        getOutfitDetailsPostCount({
          startDate,
          endDate,
          genders: selectedGenders,
          categories: selectedCategories,
          carousel: false,
        }),
        getOutfitDetailsPostCount({
          startDate,
          endDate,
          genders: selectedGenders,
          categories: selectedCategories,
          carousel: true,
        }),
      ]);

      setCounts({
        byMetaPostId: {
          withOutfit: byMetaPostId.postCount,
          total: byMetaPostId.totalCount,
        },
        byInstaShortcode: {
          withOutfit: byInstaShortcode.postCount,
          total: byInstaShortcode.totalCount,
        },
      });
    } catch (error) {
      setCounts({
        byMetaPostId: { withOutfit: null, total: null },
        byInstaShortcode: { withOutfit: null, total: null },
      });

      const status = (error as AxiosError).response?.status;
      if (status === 403) {
        toast.error("You do not have access to this feature", {
          hideProgressBar: true,
        });
      } else if (status !== 400) {
        toast.error("Failed to fetch outfit post count", {
          hideProgressBar: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const formatGenderSummary = (genders: OutfitPostCountGender[]) =>
    genders
      .map(
        gender =>
          GENDER_OPTIONS.find(option => option.value === gender)?.label ||
          gender
      )
      .join(", ");

  return (
    <div
      className={`${!isSidebarOpen ? "w-[calc(100vw-265px)]" : "w-[calc(100vw-105px)]"} fixed transition-all duration-500 ease-in-out overflow-y-scroll h-[calc(100vh-80px)] ml-1 px-6 py-5`}
    >
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-primary">
            Trend Post Count
          </h1>
          <p className="text-sm text-secondary mt-1">
            Count of posts with topwear or bottomwear data for the selected
            month.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-light shadow-sm mb-6 overflow-hidden">
          <div className="px-5 py-4 border-b border-light bg-lightGray">
            <h2 className="text-sm font-semibold text-primary">Filters</h2>
            <p className="text-xs text-tertiary mt-0.5">
              Choose a month, then run the query.
            </p>
          </div>

          <div className="p-5 space-y-5">
            <div>
              <label className="block text-secondary text-sm font-medium mb-3">
                Select Period
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-tertiary mb-1.5">Month</p>
                  <Dropdown
                    label={selectedMonth}
                    options={MONTHS}
                    onSelect={setSelectedMonth}
                  />
                </div>
                <div>
                  <p className="text-xs text-tertiary mb-1.5">Year</p>
                  <Dropdown
                    label={selectedYear}
                    options={yearOptions}
                    onSelect={setSelectedYear}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-1">
              <div>
                <label className="block text-secondary text-sm font-medium mb-2">
                  Gender
                </label>
                <div className="flex flex-wrap gap-x-5 gap-y-2 rounded-xl bg-lightGray px-4 py-3">
                  {GENDER_OPTIONS.map(({ value, label }) => (
                    <FilterCheckbox
                      key={value}
                      label={label}
                      checked={selectedGenders.includes(value)}
                      onChange={() => toggleGender(value)}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-secondary text-sm font-medium mb-2">
                  Category
                </label>
                <div className="flex flex-wrap gap-x-5 gap-y-2 rounded-xl bg-lightGray px-4 py-3">
                  {CATEGORY_OPTIONS.map(category => (
                    <FilterCheckbox
                      key={category}
                      label={category}
                      checked={selectedCategories.includes(category)}
                      onChange={() => toggleCategory(category)}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={handleRun}
                disabled={loading}
                className="h-11 px-8 rounded-xl bg-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-60"
              >
                {loading ? "Running..." : "Run"}
              </button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Skeleton height={148} borderRadius={16} />
            <Skeleton height={148} borderRadius={16} />
          </div>
        )}

        {!loading && !hasFetched && (
          <div className="bg-white rounded-2xl border border-light p-8 text-center shadow-sm">
            <p className="text-primary font-medium">No results yet</p>
            <p className="text-sm text-secondary mt-2">
              Select filters and click Run to view post counts.
            </p>
          </div>
        )}

        {!loading && hasFetched && submittedFilters && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-secondary">Results for</span>
              <span className="text-sm px-3 py-1 rounded-full bg-brandPrimarySubtle text-primary font-medium">
                {submittedFilters.monthLabel}
              </span>
              <span className="text-sm px-3 py-1 rounded-full bg-lightGray text-secondary">
                {formatGenderSummary(submittedFilters.genders)}
              </span>
              <span className="text-sm px-3 py-1 rounded-full bg-lightGray text-secondary">
                {submittedFilters.categories.join(", ")}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatCard
                title="Meta Post ID"
                description="Distinct posts by meta post ID"
                withOutfit={counts.byMetaPostId.withOutfit}
                total={counts.byMetaPostId.total}
              />
              <StatCard
                title="Instagram Shortcode"
                description="Distinct posts by carousel shortcode"
                withOutfit={counts.byInstaShortcode.withOutfit}
                total={counts.byInstaShortcode.total}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutfitPostCount;
